/* eslint-disable react/prop-types */
import { t } from "i18next";
import moment from "moment";
import { useCallback } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import DatePicker, { DateObject } from "react-multi-date-picker";

import Icons from "@app/assets/Icons";
import SelectInput from "@app/components/inputs/Select";
import TextInput from "@app/components/inputs/Text";
import Toggle from "@app/components/inputs/Toggle";
import { BranchDeploymentErrors } from "@app/types/Common.types";
import {
    AddBranchDeploymentFormData,
    AvailableVersions,
    BranchList,
} from "@app/types/VersionControl";

import "./index.scss";

export interface AddBranchDeploymentFormProps {
    formData: AddBranchDeploymentFormData;
    setFormData: React.Dispatch<React.SetStateAction<AddBranchDeploymentFormData>>;
    setIsDirty: (_value: boolean) => void;
    branchList: BranchList[];
    availableVersions: AvailableVersions[];
    errors: BranchDeploymentErrors;
    setErrors: React.Dispatch<React.SetStateAction<BranchDeploymentErrors>>;
}

const AddBranchDeploymentForm: React.FC<AddBranchDeploymentFormProps> = ({
    formData,
    setFormData,
    setIsDirty,
    branchList,
    availableVersions,
    errors,
    setErrors,
}) => {
    const clearError = useCallback(
        (field: keyof BranchDeploymentErrors) => {
            setErrors((prev) => ({ ...prev, [field]: "" }));
        },
        [setErrors],
    );

    const updateField = useCallback(
        <Key extends keyof AddBranchDeploymentFormData>(
            field: Key,
            value: AddBranchDeploymentFormData[Key],
        ) => {
            setIsDirty(true);
            setFormData((prev) => ({
                ...prev,
                [field]: value,
            }));
            clearError(field as keyof BranchDeploymentErrors);
        },
        [setIsDirty, setFormData, clearError],
    );

    const handleBranchChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const value = e.target.value;
            const selected = branchList.find((b) => b.branchName === value);
            updateField("branchName", value);
            updateField("branchCode", selected?.branchCode ?? "");
            updateField("branchId", selected?.branchId ?? "");
        },
        [branchList, updateField],
    );

    return (
        <Container fluid className='py-4 px-5'>
            <Row className='mb-4'>
                <Col xs={6}>
                    <SelectInput
                        label={t("VERSION_MANAGEMENT.ADD.NAME.LABEL")}
                        value={formData?.branchName}
                        name='name'
                        onChange={handleBranchChange}
                        options={[
                            { value: "", label: t("VERSION_MANAGEMENT.ADD.NAME.PLACEHOLDER") },
                            ...branchList.map((b) => ({
                                value: b?.branchName,
                                label: b?.branchName,
                            })),
                        ]}
                        error={errors?.branchName}
                    />
                </Col>
                <Col xs={6}>
                    <TextInput
                        label={t("VERSION_MANAGEMENT.ADD.CODE.LABEL") as string}
                        placeholder={t("VERSION_MANAGEMENT.ADD.CODE.PLACEHOLDER")}
                        value={formData?.branchCode}
                        name='branchCode'
                        disabled
                        error={errors?.branchCode}
                    />
                </Col>
            </Row>
            <Row className='mb-4'>
                <Col xs={6}>
                    <SelectInput
                        label={t("VERSION_MANAGEMENT.ADD.AVAILABLE_VERSION.LABEL")}
                        value={formData?.availableVersion}
                        name='availableVersion'
                        onChange={(e) => {
                            updateField("availableVersion", e.target.value);
                        }}
                        options={[
                            {
                                value: "",
                                label: t("VERSION_MANAGEMENT.ADD.AVAILABLE_VERSION.PLACEHOLDER"),
                            },
                            ...availableVersions.map((v) => ({
                                value: v?.id,
                                label: v?.name,
                            })),
                        ]}
                        error={errors?.availableVersion}
                    />
                </Col>
                <Col xs={6}>
                    <div
                        className={`range-picker-container ${errors.expiryDate ? "has-error" : ""}`}
                    >
                        <p className='range-picker-label'>
                            {t("VERSION_MANAGEMENT.ADD.EXPIRY_DATE.LABEL")}
                        </p>
                        <DatePicker
                            value={
                                formData?.expiryDate ? new DateObject(formData?.expiryDate) : null
                            }
                            onChange={(date: DateObject | null) => {
                                const DateValue = date ? date.format("YYYY-MM-DD") : "";
                                updateField("expiryDate", DateValue);
                            }}
                            placeholder='DD/MM/YYYY'
                            format='DD/MM/YYYY'
                            minDate={moment().startOf("day").toDate()}
                        />
                        <img
                            src={Icons.CalendarIcon}
                            alt='rangePickerIcon'
                            className='range-picker-icon'
                        />
                        {errors.expiryDate && (
                            <div className='error-text'>
                                <img
                                    src={Icons.ExclamationIcon}
                                    alt='error'
                                    className='error-icon'
                                />
                                <span>{errors.expiryDate}</span>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
            <Row className='mb-4'>
                <Col xs={6}>
                    <Form.Group className='d-flex align-items-end gap-4'>
                        <Form.Label className='text-input__label'>
                            {t("VERSION_MANAGEMENT.ADD.STATUS.LABEL")}
                        </Form.Label>
                        <Toggle
                            data-testid='toggle-switch'
                            isActive={formData?.isActive}
                            onToggle={() => updateField("isActive", !formData.isActive)}
                            display={
                                formData?.isActive
                                    ? `${t("COMMON.ACTIVE")}`
                                    : `${t("COMMON.INACTIVE")}`
                            }
                        />
                    </Form.Group>
                </Col>
            </Row>
        </Container>
    );
};

export default AddBranchDeploymentForm;
